import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Briefcase, TrendingUp } from "lucide-react";
import serviceHeroData from "./serviceHeroData";
import "./ServiceHero.css";

// Roughly the average glyph width of the bold headline face, as a fraction of
// the font size. Used to pick a font size that keeps the tagline on one line.
const AVG_GLYPH_WIDTH_EM = 0.55;

// Shared hero for every service detail page. One consistent layout,
// concise copy — descriptions are hard-capped at two lines via line-clamp.
const ServiceHero = ({ serviceId, hero }) => {
  const data = serviceHeroData[serviceId];
  if (!data) return null;

  const Icon = data.icon;
  // Percentage of the headline container's width to use per font-size unit, so
  // the tagline fills the line without wrapping. Longer taglines get a smaller
  // share; the CSS caps the result at the design size for short ones.
  const headlineScale = (
    100 /
    ((data.cardHeadline || "").length * AVG_GLYPH_WIDTH_EM)
  ).toFixed(2);
  const words = (hero.h1 || "").split(" ");
  const lastWord = words[words.length - 1];
  const leadWords = words.slice(0, -1).join(" ");

  return (
    <section
      className="relative w-full overflow-hidden bg-[#F6F4EF]"
      style={{ paddingTop: "140px", paddingBottom: "80px" }}
    >
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div
          className="absolute top-0 left-0 w-full h-full"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #1b7840 1px, transparent 0)`,
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 w-full relative z-10">
        {/* fr instead of 42%/58%: percentages plus the gap summed to more than
            the container, which overflowed and squeezed the card (and its
            tagline) on 1024–1280px laptops. Splitting at xl keeps the card wide
            enough for the tagline to stay on one line at every width above a
            phone. */}
        <div className="grid xl:grid-cols-[42fr_58fr] gap-12 xl:gap-16 items-center">
          {/* LEFT — Title, subtitle, short description, benefit chips */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 backdrop-blur-sm rounded-full border border-primary/15 shadow-sm mb-6">
              <Briefcase className="w-4 h-4 text-primary" strokeWidth={2} />
              <span className="text-[11px] md:text-xs font-bold text-primary uppercase tracking-widest">
                Professional Service
              </span>
            </div>

            {/* Title — last word highlighted in brand green */}
            <h1
              className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-[1.1] mb-6"
              style={{ letterSpacing: "-0.02em" }}
            >
              {leadWords && <span className="text-gray-900">{leadWords} </span>}
              <span className="text-primary block">{lastWord}</span>
            </h1>

            {/* Divider */}
            <div className="w-10 h-1 bg-primary rounded-full mb-6" />

            {/* Subtitle */}
            <p className="text-lg md:text-xl font-bold text-gray-800 mb-4">
              {hero.s1}
            </p>

            {/* Description — max two lines */}
            <p className="text-base md:text-lg text-gray-600 leading-relaxed line-clamp-2 mb-10">
              {data.description}
            </p>

            {/* Benefit chips */}
            <div className="grid grid-cols-4 gap-3">
              {data.benefits.map((benefit, i) => {
                const BenefitIcon = benefit.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}
                    className="flex flex-col items-center text-center gap-2"
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <BenefitIcon className="w-5 h-5 text-primary" strokeWidth={2} />
                    </div>
                    <span className="text-xs font-semibold text-gray-700 leading-tight">
                      {benefit.label}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* RIGHT — Summary card */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <div className="relative bg-white rounded-3xl border border-primary/10 shadow-xl p-8 md:p-10">
              {/* Card header */}
              <div className="flex items-start justify-between gap-6 mb-6">
                <div className="flex items-start gap-5 flex-1 min-w-0">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg"
                    style={{
                      background: "linear-gradient(135deg, #1f8a4a 0%, #155a30 100%)",
                    }}
                  >
                    <Icon className="w-7 h-7 text-white" strokeWidth={2} />
                  </div>
                  <div
                    className="serviceHeroHeadlineBox flex-1"
                    style={{ "--headline-scale": headlineScale }}
                  >
                    <h2 className="serviceHeroHeadline font-extrabold text-gray-900 leading-snug mb-3">
                      {data.cardHeadline}
                    </h2>
                    <div className="w-10 h-0.5 bg-primary/40 rounded-full" />
                  </div>
                </div>
                <TrendingUp className="w-6 h-6 text-primary flex-shrink-0 hidden md:block" strokeWidth={2} />
              </div>

              {/* Card copy — max two lines */}
              <p className="text-base md:text-lg text-gray-600 leading-relaxed line-clamp-2 mb-8">
                {data.cardText}
              </p>

              {/* Stats strip */}
              <div className="flex items-stretch justify-between bg-[#f4f9f6] border border-primary/10 rounded-2xl px-5 py-4 mb-7">
                {data.stats.map((stat, i) => {
                  const StatIcon = stat.icon;
                  return (
                    <React.Fragment key={i}>
                      {i > 0 && <div className="w-px bg-primary/10 mx-3" />}
                      <div className="flex items-center gap-3 flex-1 justify-center">
                        <div className="w-9 h-9 rounded-full border border-primary/25 flex items-center justify-center flex-shrink-0">
                          <StatIcon className="w-4 h-4 text-primary" strokeWidth={2} />
                        </div>
                        <div>
                          <div className="text-base md:text-lg font-extrabold text-gray-900 leading-none">
                            {stat.value}
                          </div>
                          <div className="text-[11px] md:text-xs text-gray-600 mt-1">
                            {stat.label}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>

              {/* CTA */}
              <Link to="/Contact" className="block">
                <motion.button
                  whileHover={{ scale: 1.01, y: -1 }}
                  whileTap={{ scale: 0.99 }}
                  className="w-full py-4 bg-primary hover:bg-primary-dark text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3"
                >
                  {data.ctaLabel}
                  <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                </motion.button>
              </Link>

              {/* Trust markers */}
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-5 text-xs md:text-sm text-gray-600">
                <span className="inline-flex items-center gap-1.5">
                  <BadgeCheck className="w-4 h-4 text-primary" strokeWidth={2} />
                  Proven Methodology
                </span>
                <span className="w-1 h-1 bg-gray-300 rounded-full" />
                <span>Measurable Results</span>
                <span className="w-1 h-1 bg-gray-300 rounded-full" />
                <span>Lasting Impact</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ServiceHero;
